import React, { useCallback } from 'react';
import { useForm } from 'react-final-form';
import { TextField } from 'components/Forms/TextField';
import { useWindowSize } from 'hooks/useWindowSize';
import { useFieldValue } from 'hooks/useFormHelpers';

interface Props {
  selectedPaymentMethod: { value: string; label: string; enabledFieldName: string };
  handleRowClick: (rowId: string) => void;
}

export const OfflinePaymentDetails = React.memo(function OfflinePaymentDetails({
  selectedPaymentMethod,
  handleRowClick,
}: Props) {
  const form = useForm();
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const paymentMethodDetails: { label: string; value: string }[] = useFieldValue(
    selectedPaymentMethod.enabledFieldName
  );

  const handleAddSection = useCallback(() => {
    const currentSections = paymentMethodDetails || [];
    const newSections = [...currentSections, { label: '', value: '' }];

    form.change(selectedPaymentMethod.enabledFieldName, newSections);
  }, [paymentMethodDetails, form, selectedPaymentMethod.enabledFieldName]);

  const handleDeleteSection = useCallback(() => {
    const currentSections = paymentMethodDetails || [];
    const selectedSectionIndex = currentSections.length - 1;
    const updatedSections = currentSections.filter((_, index) => index !== selectedSectionIndex);

    form.change(selectedPaymentMethod.enabledFieldName, updatedSections);
  }, [paymentMethodDetails, form, selectedPaymentMethod.enabledFieldName]);

  const validatePairedField = useCallback(
    (siblingKey: 'label' | 'value', index: number) => (val: string, allValues: any) => {
      const section = allValues?.[selectedPaymentMethod.enabledFieldName]?.[index];
      if (section?.[siblingKey] && !val?.trim()) return 'Required';
      return undefined;
    },
    [selectedPaymentMethod.enabledFieldName]
  );

  return (
    <div className="grid full-width">
      <div className="divider" />
      <div className="details-content">
        <span className="oath-utility gray">
          Add additional details for recipient below. All fields are optional.
        </span>
        {paymentMethodDetails?.length > 0 && (
          <div className="section-titles">
            <div className="col-md-4 section-column">
              <span className="oath-utility gray">Optional Sections (30 characters max)</span>
            </div>
            <div className="col-md-8 section-column">
              <span className="oath-utility gray">Description (recommended 30 characters max)</span>
            </div>
          </div>
        )}
        {paymentMethodDetails?.map((_section, index) => (
          <div className="row" key={index}>
            <div className="col-md-4 section-column">
              <TextField
                label={!isMobile ? '' : `Label ${index + 1} (45 characters max)`}
                name={`${selectedPaymentMethod.enabledFieldName}[${index}].label`}
                type="text"
                placeholder={`Label ${index + 1}`}
                className="gray-input"
                maxLength={45}
                validate={validatePairedField('value', index)}
              />
            </div>
            <div className="col-md-8 section-column">
              <TextField
                label={!isMobile ? '' : `Description ${index + 1} (recommended 300 characters max)`}
                name={`${selectedPaymentMethod.enabledFieldName}[${index}].value`}
                type="text"
                placeholder={`Description ${index + 1}`}
                className="gray-input"
                maxLength={300}
                validate={validatePairedField('label', index)}
              />
            </div>
          </div>
        ))}
        <div className="links-section-container">
          {paymentMethodDetails?.length > 0 && (
            <button
              type="button"
              className="oath-bodysmall link-text gray"
              onClick={e => {
                e.stopPropagation();
                handleDeleteSection();
              }}
            >
              Delete Section
            </button>
          )}
          <button
            type="button"
            className="oath-bodysmall link-text gray add-section-link"
            onClick={e => {
              e.stopPropagation();
              handleAddSection();
            }}
          >
            Add Section
          </button>
        </div>
        {isMobile && (
          <div className="close-details-mobile">
            <button
              type="button"
              className="oath-bodysmall link-text oathBlue"
              onClick={e => {
                e.stopPropagation();
                handleRowClick(selectedPaymentMethod.enabledFieldName);
              }}
            >
              Close Details
            </button>
          </div>
        )}
      </div>
    </div>
  );
});
